import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { stockService } from "../services/apiService";

const PREDEFINED_SETTINGS = [
  {
    settingKey: "lowStockThreshold",
    label: "Low Stock Alert Qty",
    description: "Show alert on dashboard when product quantity falls below this number",
    icon: "fa-exclamation-triangle",
    color: "text-orange-500",
    unit: "qty",
    min: 1,
    max: 10000,
  },
  {
    settingKey: "expiryAlertDays",
    label: "Expiry Alert Days",
    description: "Show alert on dashboard when product expires within this many days",
    icon: "fa-calendar-times",
    color: "text-red-500",
    unit: "days",
    min: 1,
    max: 365,
  },
];

const DEFAULT_FORM = { settingKey: "", settingValue: "", label: "", description: "" };

const DashboardSettings = () => {
  const [settings, setSettings]   = useState([]);
  const [visibleKeys, setVisibleKeys] = useState(["lowStockThreshold", "expiryAlertDays"]);
  const [form, setForm]           = useState(DEFAULT_FORM);
  const [editing, setEditing]     = useState(null); // holds saved setting object
  const [saving, setSaving]       = useState(false);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState(null);

  const showToast = (type, message) => setToast({ type, message });
  const getErrMsg = (err, fallback) =>
    err?.response?.data?.errorMessage ||
    err?.response?.data?.message ||
    (typeof err?.response?.data === "string" ? err.response.data : null) ||
    fallback;

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [data, fieldConfig] = await Promise.all([
        stockService.getDashboardSettings(),
        stockService.getClientFieldConfig().catch(() => []),
      ]);
      setSettings(Array.isArray(data) ? data : []);
      // Match by fieldName directly — no pageName dependency
      const fc = fieldConfig || [];
      const hidden = new Set(
        fc
          .filter((f) => f.visible === false)
          .map((f) => f.fieldName)
      );
      const keys = PREDEFINED_SETTINGS
        .map((p) => p.settingKey)
        .filter((k) => !hidden.has(k));
      setVisibleKeys(keys);
    } catch { setSettings([]); setVisibleKeys(null); }
    finally { setLoading(false); }
  };

  const loadSettings = loadAll;

  // Merge saved values with predefined metadata for display, filtered by field config
  const mergedSettings = PREDEFINED_SETTINGS
    .filter((pre) => visibleKeys.includes(pre.settingKey))
    .map((pre) => {
      const saved = settings.find((s) => s.settingKey === pre.settingKey);
      return { ...pre, ...(saved || {}), saved: !!saved };
    });

  const selectedMeta = PREDEFINED_SETTINGS.find((p) => p.settingKey === form.settingKey);

  const handleKeySelect = (settingKey) => {
    const meta   = PREDEFINED_SETTINGS.find((p) => p.settingKey === settingKey);
    const saved  = settings.find((s) => s.settingKey === settingKey);
    setForm({
      settingKey,
      settingValue: saved?.settingValue ?? "",
      label:        meta?.label        ?? "",
      description:  meta?.description  ?? "",
    });
    setEditing(saved || null);
  };

  const handleEdit = (item) => {
    setForm({
      settingKey:   item.settingKey,
      settingValue: item.settingValue ?? "",
      label:        item.label        ?? "",
      description:  item.description  ?? "",
    });
    setEditing(item);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => {
    setForm(DEFAULT_FORM);
    setEditing(null);
  };

  const validate = () => {
    if (!form.settingKey) { showToast("error", "Please select a setting"); return false; }
    const val = parseInt(form.settingValue, 10);
    if (isNaN(val) || val < 1) { showToast("error", "Value must be a positive number"); return false; }
    if (selectedMeta) {
      if (val < selectedMeta.min || val > selectedMeta.max) {
        showToast("error", `Value must be between ${selectedMeta.min} and ${selectedMeta.max}`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await stockService.saveDashboardSetting({
        settingKey:   form.settingKey,
        settingValue: String(form.settingValue).trim(),
        label:        form.label,
        description:  form.description,
      });
      showToast("success", `"${form.label || form.settingKey}" saved successfully`);
      handleReset();
      loadSettings();
    } catch (err) {
      showToast("error", getErrMsg(err, "Failed to save setting"));
    } finally { setSaving(false); }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* ── Form Section ─────────────────────────────────────────────────── */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex items-center justify-between">
          <span>{editing ? "✏️ Update Setting" : "⚙️ Configure Setting"}</span>
          {editing && (
            <button onClick={handleReset}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition flex items-center gap-1">
              <i className="fas fa-plus-circle" /> New
            </button>
          )}
        </h2>

        <div className="p-3">
          <div className="flex flex-wrap gap-3 items-end">

            {/* Setting selector */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Setting <span className="text-red-500">*</span>
              </label>
              <select
                value={form.settingKey}
                onChange={(e) => handleKeySelect(e.target.value)}
                className="w-52 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
              >
                <option value="">— Select Setting —</option>
                {PREDEFINED_SETTINGS
                  .filter((p) => visibleKeys.includes(p.settingKey))
                  .map((p) => (
                    <option key={p.settingKey} value={p.settingKey}>{p.label}</option>
                  ))}
              </select>
            </div>

            {/* Value input */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Value {selectedMeta && (
                  <span className="text-gray-400 font-normal">
                    ({selectedMeta.min}–{selectedMeta.max} {selectedMeta.unit})
                  </span>
                )} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={form.settingValue}
                  onChange={(e) => setForm((p) => ({ ...p, settingValue: e.target.value }))}
                  placeholder={selectedMeta ? `e.g. ${selectedMeta.settingKey === "lowStockThreshold" ? "10" : "30"}` : "Enter value"}
                  min={selectedMeta?.min ?? 1}
                  max={selectedMeta?.max ?? 9999}
                  className="w-36 px-3 py-1.5 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                />
                {selectedMeta && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                    {selectedMeta.unit}
                  </span>
                )}
              </div>
            </div>

            {/* Description (read-only hint) */}
            {selectedMeta && (
              <div className="flex items-end pb-1">
                <p className="text-xs text-gray-500 max-w-xs">
                  <i className={`fas ${selectedMeta.icon} ${selectedMeta.color} mr-1`} />
                  {selectedMeta.description}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={handleReset}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
              <i className="fas fa-redo" /> Reset
            </button>
            <button onClick={handleSave} disabled={saving}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                saving ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
              }`}>
              {saving
                ? <><i className="fas fa-spinner fa-spin" /> Saving...</>
                : editing
                  ? <><i className="fas fa-check" /> Update Setting</>
                  : <><i className="fas fa-save" /> Save Setting</>}
            </button>
          </div>
        </div>
      </section>

      {/* ── Settings Table ────────────────────────────────────────────────── */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-3 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          <h2 className="text-base font-bold text-blue-800">
            ⚙️ Dashboard Settings
            {mergedSettings.length > 0 && (
              <span className="text-xs font-normal text-blue-600 ml-2">
                ({mergedSettings.filter((s) => s.saved).length} configured)
              </span>
            )}
          </h2>
        </div>

        <div className="overflow-auto flex-1">
          <table className="min-w-full text-sm text-gray-800">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-5 text-left">Setting</th>
                <th className="py-2.5 px-5 text-left">Description</th>
                <th className="py-2.5 px-5 text-center">Current Value</th>
                <th className="py-2.5 px-5 text-center">Default</th>
                <th className="py-2.5 px-5 text-center">Status</th>
                <th className="py-2.5 px-5 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-blue-400">
                    <i className="fas fa-spinner fa-spin text-2xl" />
                  </td>
                </tr>
              ) : (
                mergedSettings.map((item, idx) => (
                  <tr key={item.settingKey}
                    className={`border-t transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>

                    <td className="py-3 px-5">
                      <div className="flex items-center gap-2">
                        <i className={`fas ${item.icon} ${item.color} text-sm`} />
                        <div>
                          <p className="font-semibold text-gray-800 text-xs">{item.label}</p>
                          <p className="text-xs text-gray-400 font-mono">{item.settingKey}</p>
                        </div>
                      </div>
                    </td>

                    <td className="py-3 px-5 text-xs text-gray-500 max-w-xs">{item.description}</td>

                    <td className="py-3 px-5 text-center">
                      {item.saved ? (
                        <span className="text-sm font-bold text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200">
                          {item.settingValue} {item.unit}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    <td className="py-3 px-5 text-center">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {item.settingKey === "lowStockThreshold" ? "10 qty" : "30 days"}
                      </span>
                    </td>

                    <td className="py-3 px-5 text-center">
                      {item.saved ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          <i className="fas fa-check-circle mr-1" />Configured
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          <i className="fas fa-clock mr-1" />Using Default
                        </span>
                      )}
                    </td>

                    <td className="py-3 px-5 text-center">
                      <button onClick={() => handleEdit(item.saved ? item : { ...item, settingValue: "" })}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold transition">
                        <i className="fas fa-edit mr-1" />{item.saved ? "Edit" : "Configure"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default DashboardSettings;
